import { useMemo } from 'react'
import Autocomplete from '@mui/material/Autocomplete'
import Box from '@mui/material/Box'
import TextField from '@mui/material/TextField'
import { useComboBoxSearch } from 'components/ComboBox/useComboBoxState'
import { splitOptionKey, toMuiComboBoxProps, toTextFieldProps } from 'components/ComboBox/muiProps'
import useGroupedOptions, { GroupedOption } from './useGroupedOptions'
import { ComboBoxOptionOption, GroupedComboBoxProps } from 'components/ComboBox/types'

export default function GroupedComboBoxMui<T>( {
	setSelectedOption,
	selectedOption,
	data,
	getName,
	setSearchString,
	debounceDelayMs,
	async = true,
	getGroupBy,
	getGroupLabel,
	sortCallback,
	renderOption,
	onSearchChange,
	...props
}: GroupedComboBoxProps<T> ) {
	const { inputVal, setInputVal } = useComboBoxSearch( { setSearchString, debounceDelayMs } )
	const { flatOptions, buildOption } = useGroupedOptions( { data, getName, getGroupBy, getGroupLabel, sortCallback } )
	const { autocomplete, textField, suppressOptions } = toMuiComboBoxProps( props )

	// buildOption allocates a fresh object every call; useAutocomplete resets the
	// visible input text whenever the `value` prop's reference changes. Memoizing
	// on `selectedOption` alone (not `flatOptions`, which is rebuilt on every
	// search) keeps identity stable through typing and data refetches. Building
	// `groupLabel` straight from `getGroupLabel` (rather than matching into
	// `flatOptions` by display label) also means two options that share a label
	// can no longer resolve to the wrong one.
	const value: GroupedOption<T> | null = useMemo( () => {
		if ( !selectedOption ) {
			return null
		}

		const selected = buildOption( selectedOption )

		return { ...selected, groupLabel: getGroupLabel( selected.value ) }
	}, [ selectedOption ] )

	// Pickers can hydrate a selection before its search returns, so the selected
	// option isn't always in `data`. MUI warns on a value outside `options`.
	const options = value && !flatOptions.some( ( option ) => option.value === value.value )
		? [ value, ...flatOptions ]
		: flatOptions

	return (
		<Autocomplete<GroupedOption<T>>
			{...autocomplete}
			options={suppressOptions ? [] : options}
			groupBy={( option ) => option.groupLabel}
			value={value}
			onChange={( _event, next ) => setSelectedOption( next?.value )}
			onInputChange={( _event, next, reason ) => {
				// 'reset' fires when a selection writes its label back into the
				// input; EUI's onSearchChange only reacts to user edits.
				if ( reason === 'reset' ) {
					return
				}

				setInputVal( next )
				onSearchChange?.( next )
			}}
			getOptionLabel={( option ) => option.label}
			isOptionEqualToValue={( option, current ) => option.value === current.value}
			// EUI treats `async` options as pre-filtered by the caller's query.
			filterOptions={async ? ( unfiltered ) => unfiltered : undefined}
			renderOption={renderOption
				? ( optionProps, option ) => {
					const { key, liProps } = splitOptionKey( optionProps )

					return (
						<Box component="li" key={key ?? option.label} {...liProps}>
							{renderOption( option as ComboBoxOptionOption<T>, inputVal, '' )}
						</Box>
					)
				}
				: undefined}
			renderInput={( params ) => (
				<TextField {...toTextFieldProps( params, textField )} />
			)}
		/>
	)
}
